import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import PreviewModal from './PreviewModal';

// v6 stepper forms (finance_module_stepper_forms_and_pending_items.md, Part D / §D1).
//
// A dependency-free wizard. The page still owns its single `form` object and its
// `emptyForm` / `toFormState` / `toPayload` / `handleSubmit` - the wizard only reorganises the
// field JSX into `steps[].render` and gates "Next" on `steps[].validate`. It never reshapes the
// payload.
//
// The step indicator is a connector-joined numbered rail (`.fw-steps`, styled in each vertical's
// App.css). Every dot is `flex: 1`, so the rail is exactly the container width at any viewport -
// it never overflows or scrolls horizontally. Step captions show under the dots on >= sm; on the
// narrowest screens the rail is icon-only and the current step's title is carried by the <h5>.
//
//   <FormWizard
//     steps={[{ key, title, render: (ctx) => JSX, validate: (values) => errors|null }]}
//     values={form}
//     onChange={setForm}
//     onSubmit={handleSubmit}
//     submitting={submitting}
//     submitLabel="List vehicle"
//     error={error}
//     startVisitedAll={isEdit}
//     renderPreview={(values) => <ListingPreviewCard .../>}   // shown in the Review-step popup
//   />
//
// `ctx` passed to each `render`/`validate` = { values, onChange, errors, goTo }.
export default function FormWizard({
  steps,
  values,
  onChange,
  onSubmit,
  submitting = false,
  submitLabel = 'Submit',
  error,
  startVisitedAll = false,
  renderPreview,
}) {
  const [current, setCurrent] = useState(0);
  const [visited, setVisited] = useState(
    () => new Set(startVisitedAll ? steps.map((_, i) => i) : [0])
  );
  const [errors, setErrors] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const headingRef = useRef(null);
  const errorRef = useRef(null);
  const mounted = useRef(false);

  // Edit mode can flip startVisitedAll to true after the async load resolves.
  const stepCount = steps.length;
  useEffect(() => {
    if (startVisitedAll) setVisited(new Set(Array.from({ length: stepCount }, (_, i) => i)));
  }, [startVisitedAll, stepCount]);

  // a11y: move focus to the step heading on every change after the first render.
  useEffect(() => {
    if (mounted.current) headingRef.current?.focus();
    else mounted.current = true;
  }, [current]);

  const isLast = current === steps.length - 1;
  const step = steps[current];
  const ctx = useMemo(
    () => ({ values, onChange, errors, goTo }),
    [values, onChange, errors] // eslint-disable-line react-hooks/exhaustive-deps
  );

  function goTo(idx) {
    if (idx < 0 || idx >= steps.length) return;
    setErrors(null);
    setVisited((prev) => {
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
    setCurrent(idx);
  }

  function handleNext() {
    const stepErrors = step.validate ? step.validate(values) : null;
    if (stepErrors && Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    goTo(current + 1);
  }

  const errorList = errors ? Object.values(errors).filter(Boolean) : [];

  useEffect(() => {
    if (!error && !errorList.length) return;
    const node = errorRef.current;
    node?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    node?.focus();
  }, [error, errors]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {error && <Alert variant="danger" ref={errorRef} tabIndex={-1} role="alert">{error}</Alert>}

      <nav aria-label="Progress">
        <ol className="fw-steps">
          {steps.map((s, i) => {
            const isCurrent = i === current;
            const isDone = i < current && visited.has(i);
            const reachable = visited.has(i) && !isCurrent;
            return (
              <li
                key={s.key}
                className={`fw-step${isCurrent ? ' is-current' : ''}${isDone ? ' is-done' : ''}`}
              >
                <button
                  type="button"
                  className="fw-dot"
                  aria-label={`Step ${i + 1}: ${s.title}`}
                  aria-current={isCurrent ? 'step' : undefined}
                  aria-disabled={visited.has(i) ? undefined : 'true'}
                  onClick={() => reachable && goTo(i)}
                >
                  <span aria-hidden="true">{isDone ? '✓' : i + 1}</span>
                </button>
                <span className="fw-step-label" aria-hidden="true">{s.title}</span>
              </li>
            );
          })}
        </ol>
      </nav>

      <h5 tabIndex={-1} ref={headingRef} className="mb-3 mt-1" style={{ outline: 'none' }}>
        <span className="text-muted fw-normal small">
          Step {current + 1} of {steps.length}:
        </span>{' '}
        {step.title}
      </h5>

      <div aria-live="polite">
        {errorList.length > 0 && (
          <Alert variant="danger" className="py-2" ref={error ? undefined : errorRef} tabIndex={-1} role="alert">
            {errorList.length === 1 ? (
              errorList[0]
            ) : (
              <ul className="mb-0 ps-3">
                {errorList.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}
      </div>

      <div>{step.render(ctx)}</div>

      <div className="d-flex flex-wrap gap-2 mt-4">
        {current > 0 && (
          <Button variant="outline-secondary" onClick={() => goTo(current - 1)} disabled={submitting}>
            Back
          </Button>
        )}
        {!isLast && (
          <Button variant="primary" onClick={handleNext}>
            Next
          </Button>
        )}
        {isLast && (
          <>
            {renderPreview && (
              <Button variant="outline-primary" onClick={() => setShowPreview(true)} disabled={submitting}>
                Preview
              </Button>
            )}
            <Button variant="primary" onClick={() => onSubmit()} disabled={submitting}>
              {submitting ? 'Saving…' : submitLabel}
            </Button>
          </>
        )}
      </div>

      {renderPreview && (
        <PreviewModal
          show={showPreview}
          title="Preview"
          onHide={() => setShowPreview(false)}
          onSubmit={onSubmit}
          submitting={submitting}
          submitLabel={submitLabel}
        >
          {renderPreview(values)}
        </PreviewModal>
      )}
    </div>
  );
}
